import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { CREDIT_VALUE_IN_POUNDS } from "@/configs";
import { Coins } from "lucide-react";

interface EventCreditInfoProps {
  creditsBalance: number;
  totalCreditCost: number;
  originalCreditCost?: number;
  eventPrice: number;
  effectivePrice?: number;
  seats: number;
  applyCredits: boolean;
  onToggleCredits: (apply: boolean) => void;
  discountLabel?: string;
}

const EventCreditInfo = ({
  creditsBalance,
  totalCreditCost,
  originalCreditCost,
  eventPrice,
  effectivePrice,
  seats,
  applyCredits,
  onToggleCredits,
  discountLabel,
}: EventCreditInfoProps) => {
  const totalPrice = effectivePrice ?? (eventPrice * seats);
  
  // Calculate credits to use (limited by balance and total cost)
  const creditsToUse = Math.min(creditsBalance, totalCreditCost);
  const creditValueUsed = creditsToUse * CREDIT_VALUE_IN_POUNDS;
  
  // Calculate remaining to pay
  const remainingToPay = Math.max(0, totalPrice - creditValueUsed);
  const isFullyCovered = remainingToPay <= 0;

  if (creditsBalance <= 0) {
    return null;
  }

  return (
    <div className="mb-4 bg-muted/50 border border-border rounded-lg p-3">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Coins className="w-4 h-4 text-primary" />
          <span className="text-sm font-medium">Your credits</span>
        </div>
        <Badge variant="outline">{creditsBalance} credits</Badge>
      </div>
      
      <div className="flex items-center justify-between">
        <Label htmlFor="apply-credits" className="text-sm cursor-pointer">
          Use credits
        </Label>
        <Switch
          id="apply-credits"
          checked={applyCredits}
          onCheckedChange={onToggleCredits}
        />
      </div>

      {applyCredits && (
        <div className="mt-3 pt-3 border-t border-border space-y-1 text-sm">
          <div className="flex justify-between text-muted-foreground">
            <span>Credits to use</span>
            <span className="flex items-center gap-1">
              {originalCreditCost && originalCreditCost > totalCreditCost && (
                <span className="line-through opacity-60">{originalCreditCost}</span>
              )}
              {creditsToUse} (£{creditValueUsed.toFixed(2)} value)
            </span>
          </div>
          {discountLabel && (
            <div className="flex justify-between text-success text-xs">
              <span>{discountLabel}</span>
            </div>
          )}
          {isFullyCovered ? (
            <div className="flex justify-between text-success font-medium">
              <span>Fully covered by credits!</span>
              <span>No card needed</span>
            </div>
          ) : (
            <div className="flex justify-between text-foreground font-medium">
              <span>Remaining to pay</span>
              <span>£{remainingToPay.toFixed(2)} by card</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default EventCreditInfo;
